import {Component, inject, OnInit} from "@angular/core";
import {ActivatedRoute, Router} from "@angular/router";
import {PaymentMethod, Stripe, StripeCardElement, StripeElements, StripeIbanElement} from '@stripe/stripe-js';
import * as paymentIntents from '@stripe/stripe-js/dist/stripe-js/payment-intents';
import { AuthenticationService } from "../../../../../../core/services/authentication.service";
import { StripeService } from "../../../../../../core/services/stripe.service";
import { Subscription } from "../../../../../../core/models/subscription.model";
import { Coupon } from "../../../../../../core/models/coupon.model";
import { PaymentStarted } from "../../../../../../core/models/payment/payment-started.model";
import { Addon } from "../../../../../../core/models/addon.model";
import { state } from "@angular/animations";

@Component({
  selector: 'admin-onboarding-summary',
  templateUrl: './admin-onboarding-summary.page.html',
  styleUrls: ['./admin-onboarding-summary.page.scss']
})
export class AdminOnboardingSummaryPage implements OnInit {

  readonly #router = inject(Router);
  readonly #activatedRoute = inject(ActivatedRoute);
  readonly #memberConnected = inject(AuthenticationService).member;
  readonly #stripeService = inject(StripeService);

  readonly #communityId = this.#activatedRoute.snapshot.params['communityId'];
  private _subscriptionData: Subscription | null;

  private _stripe: Stripe | null = null;
  private _elements: StripeElements | null = null;
  private _card: StripeCardElement | null = null;
  private _iban: StripeIbanElement | null = null;
  private _coupon: Coupon | null = null;
  private _paymentProcessing = false;
  private _selectedPaymentMethod: 'card' | 'sepa' = 'card';
  private _paying = false;

  constructor() {
    const routeState = this.#router.getCurrentNavigation()?.extras.state;
    this._subscriptionData = routeState?.['subscription'] ?? null;
    if (!this._subscriptionData) {
      this.navigateTo('subscription');
    }
  }

  async ngOnInit() {
    this._stripe = await this.#stripeService.stripe;
    if (this._stripe) {
      this._elements = this._stripe.elements();
      this._card = this._elements.create('card');
      this._iban = this._elements.create('iban', {supportedCountries: ['SEPA'], placeholderCountry: 'FR'});
      this._card.mount('#card-element');
      this._iban.mount('#sepa-element');
    }

    console.log({connectedMember: this.#memberConnected});
  }

  previousStep(): void {
    this._paying ? (this._paying = false) : this.navigateTo('subscription');
  }

  private navigateTo(state: string): void {
    this.#router.navigate(["/admin/onboarding", this.#communityId, state]);
  }

  async submitPayment() {
    if (!this._stripe || (!this._card && !this._iban) || this._paymentProcessing) return;
    this._paymentProcessing = true;

    const paymentMethodData: paymentIntents.CreatePaymentMethodData = this._selectedPaymentMethod === 'card' ? {
      type: 'card',
      card: this._card!,
    } : {
      type: 'sepa_debit',
      sepa_debit: this._iban!,
      billing_details: {
        name: `${this.#memberConnected!.profile.lastName} ${this.#memberConnected!.profile.firstName}`,
        email: this.#memberConnected!.email,
      }
    };

    const paymentMethodResult = await this._stripe.createPaymentMethod(paymentMethodData);

    if (paymentMethodResult.error) {
      console.error(paymentMethodResult.error);
      return;
    }

    this.#stripeService.startPaymentProcess(
      this._subscriptionData!.id,
      this.#communityId,
      this._coupon?.id,
      paymentMethodResult.paymentMethod?.id
    ).subscribe(
      (paymentStarted) => this.finalizePayment(paymentStarted, paymentMethodResult.paymentMethod),
      (error) => {
        console.error(error);
        this.navigateTo('payment-error');
      }
    );
  }

  private async finalizePayment(paymentStarted: PaymentStarted, paymentMethod?: PaymentMethod) {
    const paymentMethodId = paymentMethod?.id;
    if (!this._stripe || !paymentMethodId) return;

    const result = await (this._selectedPaymentMethod === 'card'
      ? this._stripe.confirmCardPayment(paymentStarted.clientSecret, {payment_method: paymentMethodId})
      : this._stripe.confirmSepaDebitPayment(paymentStarted.clientSecret, {payment_method: paymentMethodId}));

    if (!result) {
      console.log('payment result is undefined...');
      return;
    }

    if (result.error) {
      console.error(result.error);
      this.navigateTo('payment-error');
      return;
    }

    console.log('Payment success:', result);
    this.navigateTo(result.paymentIntent?.status === 'succeeded' ? 'payment-success' : 'payment-processing');
  }

  set updateCoupon(coupon: Coupon) {
    this._coupon = coupon;
  }

  get coupon(): Coupon | null {
    return this._coupon;
  }

  get subscriptionData(): Subscription | null {
    return this._subscriptionData;
  }

  get isPaymentProcessing(): boolean {
    return this._paymentProcessing;
  }

  get currentPaymentMethod(): 'card' | 'sepa' {
    return this._selectedPaymentMethod;
  }

  set currentPaymentMethod(method: 'card' | 'sepa') {
    this._selectedPaymentMethod = method;
  }

  get isPaying(): boolean {
    return this._paying;
  }

  showPayment(): void {
    this._paying = true;
  }

  get totalWithTaxes(): number {
    return +(this._subscriptionData?.addons.reduce((sum, addon) => sum + addon.basePrice, 0) ?? 0).toFixed(2);
  }

  getSubscriptionDiscountedPrice(): number {
    if (!this._subscriptionData) return 0;

    const basePrice = this._subscriptionData.basePrice;

    if (this._coupon?.reduction) {
      const reductionPercent = parseFloat(this._coupon.reduction);
      return Math.max(0, basePrice * (1 - reductionPercent / 100));
    }

    return basePrice;
  }

  getAddonDiscountedPrice(addon: Addon): number {
    const basePrice = addon.basePrice;

    const matched = this._coupon?.couponAddons?.find(a => a.addonId === addon.id);
    if (matched) {
      return Math.max(0, basePrice * (1 - matched.reduction / 100));
    }

    return basePrice;
  }
}
